import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlus } from 'react-icons/fa6';
import { ProfileDataType, ProfileField } from '../../../types/profile';
import { profileService } from '../../../services';
import useProfileFields from '../../../hooks/useProfileFields';
import ProfileFieldModal from '../../modals/ProfileFieldModal';
import Field from './Field';
import Moment from './Moment';
import Social from './Social';
import InlineFieldInput from './InlineFieldInput';
import useAuth from '../../../hooks/useAuth';
import Button from '../../misc/Button';
import { DashboardMemberFieldType } from '../../../types/invites';

interface UserDataFormProps {
  selectedData: ProfileField[];
  setSelectedData: (selectedData: ProfileField[]) => void;
  nonRequestedFields?: DashboardMemberFieldType[];
  mandatoryFields?: DashboardMemberFieldType[];
  onePerDataType?: boolean;
  hasSelectAll?: boolean;
  onlyBirthDate?: boolean;
  newFields?: Partial<Record<ProfileDataType, ProfileField>>;
  onNewFieldsChange?: (fields: Partial<Record<ProfileDataType, ProfileField>>) => void;
  preSelectAll?: boolean;
}

export default function UserDataForm({
  selectedData,
  setSelectedData,
  nonRequestedFields = [],
  mandatoryFields = [],
  onePerDataType = false,
  hasSelectAll = false,
  onlyBirthDate = false,
  newFields = {},
  onNewFieldsChange,
  preSelectAll = false,
}: UserDataFormProps): JSX.Element {
  const profileFields = useProfileFields();

  const { t } = useTranslation();
  const auth = useAuth();

  const emails = profileFields.filter((x) => x.dataType === ProfileDataType.EMAIL);
  const phoneNumbers = profileFields.filter((x) => x.dataType === ProfileDataType.PHONENUMBER);
  const addresses = profileFields.filter((x) => x.dataType === ProfileDataType.ADDRESS);
  const businessname = profileFields.filter(
    (x) => !nonRequestedFields.includes(ProfileDataType.BUSINESSNAME) && x.dataType === ProfileDataType.BUSINESSNAME,
  );
  const moments = profileFields.filter(
    (x) => (!onlyBirthDate && x.dataType === ProfileDataType.MOMENT) || x.dataType === ProfileDataType.BIRTHDATE,
  );
  const socialmedia = profileFields.filter(
    (x) => x.dataType === ProfileDataType.SOCIAL && x.social && !nonRequestedFields.includes(x.social?.socialType),
  );

  const hasEmail = !nonRequestedFields.includes(ProfileDataType.EMAIL);
  const hasPhoneNumber = !nonRequestedFields.includes(ProfileDataType.PHONENUMBER);
  const hasAddress = !nonRequestedFields.includes(ProfileDataType.ADDRESS);
  const hasMoment = !nonRequestedFields.includes(ProfileDataType.BIRTHDATE);

  const [modalDataType, setModalDataType] = useState<ProfileDataType>(ProfileDataType.EMAIL);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (!auth) return;
    profileService.getUserData();
  }, [auth?.email]);

  // Pre-select all fields when preSelectAll is true
  useEffect(() => {
    if (preSelectAll && profileFields.length > 0 && selectedData.length === 0) {
      // Filter out non-requested fields and apply other filters
      const selectableFields = profileFields.filter(nonRequestedFilter);
      setSelectedData(selectableFields);
    }
  }, [preSelectAll, profileFields, selectedData.length, setSelectedData]);

  const handleSelectAll = (): void => {
    if (selectedData.length === profileFields.length) setSelectedData([]);
    else setSelectedData(profileFields);
  };

  const nonRequestedFilter = (field: ProfileField): boolean => !nonRequestedFields.includes(field.dataType);

  const handleClick = (active: boolean, field: ProfileField): void => {
    if (active) {
      setSelectedData(selectedData.filter((x) => x.id !== field.id));
    } else if (onePerDataType && field.dataType !== ProfileDataType.SOCIAL)
      setSelectedData([...selectedData.filter((x) => x.dataType !== field.dataType), field]);
    else setSelectedData([...selectedData, field]);
  };

  const handleProfileAdd = (type: ProfileDataType): void => {
    setModalDataType(type);
    setModalOpen(true);
  };

  const handleNewFieldChange = (dataType: ProfileDataType, field: ProfileField): void => {
    if (onNewFieldsChange) {
      onNewFieldsChange({ ...newFields, [dataType]: field });
    }
  };

  const renderField = (f: ProfileField): JSX.Element => (
    <Field field={f} handleClick={handleClick} active={selectedData.map((x) => x.id!).includes(f.id!)} />
  );

  const renderSocial = (field: ProfileField): JSX.Element => (
    <>
      {field.social && (
        <Social
          mandatory={mandatoryFields.includes(field.social.socialType)}
          field={field}
          handleClick={handleClick}
          active={selectedData.map((x) => x.id!).includes(field.id!)}
        />
      )}
    </>
  );

  const renderMoment = (field: ProfileField) => (
    <Moment field={field} handleClick={handleClick} active={selectedData.map((x) => x.id!).includes(field.id!)} />
  );

  const addStar = (type: DashboardMemberFieldType): string => {
    if (mandatoryFields.includes(type)) return '*';
    return '';
  };

  return (
    <>
      <div className="flex flex-col gap-2 text-sm">
        {hasSelectAll && (
          <div className="flex justify-end">
            <div className="flex w-fit cursor-pointer items-center gap-2" onClick={handleSelectAll}>
              <p className="border-b border-primary-300 font-medium">{t('form.userData.selectAll')}</p>
              <input
                type="checkbox"
                checked={selectedData.length === profileFields.length}
                className="!border !border-primary"
              />
            </div>
          </div>
        )}

        {hasEmail && (
          <div className={hasAddress || hasPhoneNumber || hasMoment ? 'border-b border-secondary-50' : ''}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium">
                {t('form.userData.label.email')} {addStar(ProfileDataType.EMAIL)}
              </h2>
              {emails.length > 0 && (
                <Button variant="tertiary" className="!p-0" onClick={() => handleProfileAdd(ProfileDataType.EMAIL)}>
                  <FaPlus className="h-8 w-8 rounded-full bg-secondary p-2 text-primary" />
                </Button>
              )}
            </div>
            {emails.length > 0 ? (
              <div className="my-1 mb-2 flex flex-col gap-2">{emails.filter(nonRequestedFilter).map(renderField)}</div>
            ) : (
              <InlineFieldInput
                dataType={ProfileDataType.EMAIL}
                onFieldChange={(field) => handleNewFieldChange(ProfileDataType.EMAIL, field)}
                field={newFields[ProfileDataType.EMAIL]}
              />
            )}
          </div>
        )}

        {hasPhoneNumber && (
          <div className={hasAddress || hasMoment ? 'border-b border-secondary-50' : ''}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium">
                {t('form.userData.label.phoneNumber')} {addStar(ProfileDataType.PHONENUMBER)}
              </h2>
              {phoneNumbers.length > 0 && (
                <Button
                  variant="tertiary"
                  className="!p-0"
                  onClick={() => handleProfileAdd(ProfileDataType.PHONENUMBER)}>
                  <FaPlus className="h-8 w-8 rounded-full bg-secondary p-2 text-primary" />
                </Button>
              )}
            </div>
            {phoneNumbers.length > 0 ? (
              <div className="my-1 flex flex-col gap-2">{phoneNumbers.filter(nonRequestedFilter).map(renderField)}</div>
            ) : (
              <InlineFieldInput
                dataType={ProfileDataType.PHONENUMBER}
                onFieldChange={(field) => handleNewFieldChange(ProfileDataType.PHONENUMBER, field)}
                field={newFields[ProfileDataType.PHONENUMBER]}
              />
            )}
          </div>
        )}
        {hasAddress && (
          <div className="border-b border-secondary-50">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium">
                {t('form.userData.label.address')} {addStar(ProfileDataType.ADDRESS)}
              </h2>
              {addresses.length > 0 && (
                <Button variant="tertiary" className="!p-0" onClick={() => handleProfileAdd(ProfileDataType.ADDRESS)}>
                  <FaPlus className="h-8 w-8 rounded-full bg-secondary p-2 text-primary" />
                </Button>
              )}
            </div>
            {addresses.length > 0 ? (
              <div className="my-1 flex flex-col gap-2">{addresses.filter(nonRequestedFilter).map(renderField)}</div>
            ) : (
              <InlineFieldInput
                dataType={ProfileDataType.ADDRESS}
                onFieldChange={(field) => handleNewFieldChange(ProfileDataType.ADDRESS, field)}
                field={newFields[ProfileDataType.ADDRESS]}
              />
            )}
          </div>
        )}
        {hasMoment && (
          <div className={socialmedia.length > 0 ? 'border-b border-secondary-50' : ''}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium">
                {t('form.userData.label.birthDate')} {addStar(ProfileDataType.BIRTHDATE)}
              </h2>
              {moments.length > 0 && (
                <Button variant="tertiary" className="!p-0" onClick={() => handleProfileAdd(ProfileDataType.BIRTHDATE)}>
                  <FaPlus className="h-8 w-8 rounded-full bg-secondary p-2 text-primary" />
                </Button>
              )}
            </div>
            {moments.length > 0 ? (
              <div className="my-1 flex flex-col gap-2">{moments.map(renderMoment)}</div>
            ) : (
              <InlineFieldInput
                dataType={ProfileDataType.BIRTHDATE}
                onFieldChange={(field) => handleNewFieldChange(ProfileDataType.BIRTHDATE, field)}
                field={newFields[ProfileDataType.BIRTHDATE]}
              />
            )}
          </div>
        )}
        {!nonRequestedFields.includes(ProfileDataType.BUSINESSNAME) && (
          <div className={hasMoment ? 'border-b border-secondary-50' : ''}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium">
                {t('form.userData.label.businessName')} {addStar(ProfileDataType.BUSINESSNAME)}
              </h2>
              {businessname.length > 0 && (
                <Button
                  variant="tertiary"
                  className="!p-0"
                  onClick={() => handleProfileAdd(ProfileDataType.BUSINESSNAME)}>
                  <FaPlus className="h-8 w-8 rounded-full bg-secondary p-2 text-primary" />
                </Button>
              )}
            </div>
            {businessname.length > 0 ? (
              <div className="my-1 flex flex-col gap-2">{businessname.map(renderField)}</div>
            ) : (
              <InlineFieldInput
                dataType={ProfileDataType.BUSINESSNAME}
                onFieldChange={(field) => handleNewFieldChange(ProfileDataType.BUSINESSNAME, field)}
                field={newFields[ProfileDataType.BUSINESSNAME]}
              />
            )}
          </div>
        )}
        {socialmedia.length > 0 && (
          <div>
            <h2 className="text-lg font-medium">{t('form.userData.label.social')}</h2>
            <div className="my-1 flex flex-col gap-2">{socialmedia.map(renderSocial)}</div>
          </div>
        )}
      </div>
      <ProfileFieldModal
        onSave={(f) => handleClick(false, f)}
        modalType="CREATE"
        type={modalDataType}
        open={modalOpen}
        setOpen={setModalOpen}
      />
    </>
  );
}
